import type { QueryClient } from '@tanstack/react-query';
import { stripHtml } from '@worldcoo/shared/utils';
import { isDocumentTranslated, wasOurTextReplaced, type TextReplacement } from './frontend-error-filter';

/**
 * Records text the browser environment replaces while React is hydrating, so a hydration mismatch can
 * be attributed to a page translator / content blocker / in-app-WebView injection instead of to us.
 *
 * WHY A LOG AND NOT A LOOK AT THE DOM. The DOM cannot answer "was this page rewritten?" at any single
 * moment. Before `hydrateRoot` is too early: hydration runs inside a `startTransition`, so it is
 * concurrent and a rewrite lands part-way through it. When the error is reported is too late: React's
 * recovery restores the very text you would compare against — measured, the `<h1>` reads its original
 * English again after a translator rewrote it. A mutation record survives both, because it captures
 * the transition rather than the state.
 *
 * WHY AN INLINE SCRIPT AND NOT THIS MODULE. The client entry is `<script type="module" async>`, so on
 * the slow devices where this bug actually happens it executes AFTER the rewriter has finished — an
 * observer installed from the entry module recorded exactly zero of the five rewrites it was built to
 * catch (measured on iOS against a production build). The observer therefore has to be armed by a
 * classic inline script in `<head>`, which the parser runs before any module. Same reason
 * `initAssetUrl` is inlined in `__root.tsx`, and the constraint from that file applies here too: the
 * text must be a constant, byte-identical on both sides, or TanStack's script dedup fails and the
 * whole bootstrap is re-executed on every hydration.
 *
 * WHAT IS DELIBERATELY NOT WATCHED. `childList` is off. Its only use would be spotting the `<font>`
 * wrappers desktop Google Translate inserts, and the wave this was built for produces none (it
 * rewrites text in place). Subscribing would mean work for every node the parser appends, on precisely
 * the low-end devices that lose the hydration race to begin with — all cost, no signal.
 */

/** Where the inline recorder parks its findings. Read only through {@link recordedTextReplacements}. */
const REWRITE_LOG_KEY = '__wcTextRewrites';

/**
 * Raised by the recorder when it hits {@link MAX_RECORDS} and disconnects. Without it, a log that
 * stopped recording and a page that stopped mutating are the same silence — `replacements=` in the
 * attribution would read as a total when it is only a floor, on exactly the churn-heavy pages most
 * likely to bury a real rewrite past the cap.
 */
const REWRITE_LOG_CAPPED_KEY = '__wcTextRewritesCapped';

/**
 * Raised by {@link markHydrationStarted} the instant before `hydrateRoot`, and stamped onto every
 * record. A replacement logged while this was still down provably predates any React DOM write, so it
 * cannot be React repairing a mismatch — it is the one class of evidence that needs no witness at all.
 *
 * Only counted, not yet acted on: the measured wave rewrites *before* hydration, but hydration is
 * concurrent and a rewrite can also land mid-flight, so narrowing the gate to pre-hydration records
 * could disable the attribution on devices this cannot be re-measured against. The counts ride in the
 * report (see {@link describeHydrationRewrite}) so the next real ticket settles it with data.
 */
const HYDRATION_STARTED_KEY = '__wcHydrating';

/**
 * Bounded so a runaway loop or a hostile script cannot grow the log without limit on a phone. Set well
 * above what a real page produces: one campaign page logged 27 entries, most of them React's Suspense
 * placeholders, against 5 actual rewrites — the cap must never be reached before a rewrite is seen.
 * When it IS reached the recorder raises {@link REWRITE_LOG_CAPPED_KEY} on its way out, so the report
 * can say the log is a floor rather than a total.
 */
const MAX_RECORDS = 300;

/** Enough for a witness to still match on its prefix; short enough that a page-sized node cannot bloat the log. */
const MAX_VALUE_LENGTH = 512;

/** Hydration is over long before this; afterwards ordinary DOM churn must not be able to silence a report. */
const WATCH_WINDOW_MS = 10_000;

/**
 * The inline recorder. Deliberately DUMB: it records every `characterData` mutation verbatim and makes
 * no judgements, so the rules about what counts as a foreign rewrite live in
 * {@link isForeignTextReplacement} — one tested implementation rather than a copy stranded inside a
 * string. Kept as a single constant so the emitted text is identical on server and client.
 *
 * The one thing it adds beyond a verbatim record is a per-node id (`d`), because node identity cannot
 * be recovered from the strings later: `nodeValue` is sampled when the observer callback FLUSHES, so
 * every record one node produced within a batch carries the batch's final value as its `after`. The id
 * is what lets {@link recordedTextReplacements} rebuild each record's true `after` — in tested code,
 * where that rule belongs — from the next same-node record's `before`.
 */
export const HYDRATION_REWRITE_MONITOR_SCRIPT =
	`(function(){try{` +
	`var K='${REWRITE_LOG_KEY}',L=window[K]=window[K]||[];` +
	`if(typeof MutationObserver==='undefined')return;` +
	`var M=new WeakMap(),n=0;` +
	`var o=new MutationObserver(function(rs){for(var i=0;i<rs.length;i++){` +
	`if(L.length>=${MAX_RECORDS}){window['${REWRITE_LOG_CAPPED_KEY}']=1;o.disconnect();return}` +
	`var r=rs[i];if(r.type!=='characterData')continue;var t=r.target;` +
	`var d=M.get(t);if(d==null)M.set(t,d=n++);` +
	`L.push([t.nodeType,(r.oldValue==null?'':r.oldValue).slice(0,${MAX_VALUE_LENGTH}),(t.nodeValue==null?'':t.nodeValue).slice(0,${MAX_VALUE_LENGTH}),window['${HYDRATION_STARTED_KEY}']?0:1,d])` +
	`}});` +
	`o.observe(document,{characterData:true,characterDataOldValue:true,subtree:true});` +
	`setTimeout(function(){try{o.disconnect()}catch(e){}},${WATCH_WINDOW_MS})` +
	`}catch(e){}})();`;

/** React's Suspense streaming placeholders are COMMENT nodes; only real text can be a rewrite. */
const TEXT_NODE = 3;

/**
 * Whether one recorded `characterData` mutation is a foreign rewrite, as opposed to React's or the
 * parser's own bookkeeping. Pure and exported so the two exclusions that matter are covered by tests
 * rather than by a DOM harness this project has no environment for.
 */
export function isForeignTextReplacement(nodeType: number, before: string, after: string): boolean {
	// Comments, not text: React rewrites its Suspense placeholders in place (`<!--$?-->` → `<!--$~-->`
	// → `<!--$-->`) and `characterData` fires for those too — 22 of them on one campaign page against 5
	// real rewrites. Filtering on node type removes every one, and is sturdier than matching the `$`
	// sentinels, which are React's private encoding and free to change.
	if (nodeType !== TEXT_NODE) return false;
	if (before === after) return false;
	// The parser delivers a streamed text node by APPENDING to it, firing `characterData` with
	// `oldValue` a prefix of the new value. That is the document arriving, not being rewritten.
	if (after.startsWith(before)) return false;
	return true;
}

/** A replacement as it comes out of the log, carrying when it happened as well as what it did. */
export interface RecordedTextReplacement extends TextReplacement {
	/** Whether it was logged before the app called `hydrateRoot`. See {@link HYDRATION_STARTED_KEY}. */
	readonly preHydration: boolean;
}

/**
 * Raise the flag the recorder stamps onto records from here on. Called by the client entry immediately
 * before `hydrateRoot`, and nowhere else; guarded, because failing to mark must never cost the boot.
 */
export function markHydrationStarted(): void {
	try {
		if (typeof window === 'undefined') return;
		(window as unknown as Record<string, unknown>)[HYDRATION_STARTED_KEY] = true;
	} catch {
		// A frozen `window` costs the phase breakdown in the report, nothing else.
	}
}

/** The foreign text replacements recorded during hydration, for {@link wasOurTextReplaced}. */
export function recordedTextReplacements(): readonly RecordedTextReplacement[] {
	try {
		if (typeof window === 'undefined') return [];
		const raw = (window as unknown as Record<string, unknown>)[REWRITE_LOG_KEY];
		if (!Array.isArray(raw)) return [];
		const parsed: {
			nodeType: number;
			before: string;
			after: string;
			preHydration: boolean;
			nodeId: number | undefined;
		}[] = [];
		for (const entry of raw) {
			if (!Array.isArray(entry) || entry.length < 3) continue;
			const [nodeType, before, after, preHydration, nodeId] = entry as [unknown, unknown, unknown, unknown, unknown];
			if (typeof nodeType !== 'number' || typeof before !== 'string' || typeof after !== 'string') continue;
			parsed.push({
				nodeType,
				before,
				after,
				preHydration: preHydration === 1,
				// Missing on tuples from an older recorder: the record survives, only un-chained below.
				nodeId: typeof nodeId === 'number' ? nodeId : undefined,
			});
		}
		// The recorder samples `nodeValue` at callback-flush time, so every record one node produced
		// within a batch carries the batch's FINAL value as `after`. The true `after` of each record is
		// the NEXT same-node record's `before` — one node's mutations chain exactly, batches included —
		// reconstructed here in one backward pass. Without it, a translator rewrite that React repairs in
		// the SAME batch flattens to ours→ours and the rewrite disappears from the log: the repair keeps
		// reporting (fails open), but the attribution loses the very record that named the translator.
		const nextBeforeByNode = new Map<number, string>();
		for (let i = parsed.length - 1; i >= 0; i--) {
			const record = parsed[i]!;
			if (record.nodeId === undefined) continue;
			const nextBefore = nextBeforeByNode.get(record.nodeId);
			if (nextBefore !== undefined) record.after = nextBefore;
			nextBeforeByNode.set(record.nodeId, record.before);
		}
		const replacements: RecordedTextReplacement[] = [];
		for (const { nodeType, before, after, preHydration } of parsed) {
			if (isForeignTextReplacement(nodeType, before, after)) {
				replacements.push({ before, after, preHydration });
			}
		}
		return replacements;
	} catch {
		return [];
	}
}

/** Whether the recorder hit {@link MAX_RECORDS} and disconnected: the log is a floor, not a total. */
function wasRewriteLogCapped(): boolean {
	try {
		if (typeof window === 'undefined') return false;
		return (window as unknown as Record<string, unknown>)[REWRITE_LOG_CAPPED_KEY] === 1;
	} catch {
		return false;
	}
}

/**
 * Strings the app can prove it rendered, harvested from the dehydrated query cache — the same data the
 * server built this document from. They are what {@link wasOurTextReplaced} matches recorded rewrites
 * against: a replacement that took one of these away and put something unaccounted for in its place
 * was not us.
 *
 * The cache is ENUMERATED rather than read by key. Rebuilding a query key here would couple the
 * reporter to the exact shape of `getCampaign`'s input (portalId, locale, the `?lang` override), and
 * any drift would silently return nothing — disabling the attribution without failing anywhere.
 * Duck-typing on `texts` means any campaign in the cache can serve.
 *
 * `stripHtml` mirrors what the page actually renders: the hero prints `stripHtml(corporateTitle)`, and
 * `resumeShort` is injected as HTML, so both have to be compared as the DOM's text rather than as the
 * stored value. Fully guarded — losing a witness may cost an attribution, never a report.
 */
function renderedTextWitnesses(queryClient: QueryClient | undefined): string[] {
	try {
		const cache = queryClient?.getQueryCache();
		if (!cache) return [];
		const witnesses: string[] = [];
		for (const query of cache.getAll()) {
			const texts = (query.state.data as { texts?: { corporateTitle?: string; resumeShort?: string } } | undefined)
				?.texts;
			if (!texts) continue;
			if (texts.corporateTitle) witnesses.push(stripHtml(texts.corporateTitle));
			if (texts.resumeShort) witnesses.push(stripHtml(texts.resumeShort));
		}
		return witnesses;
	} catch {
		return [];
	}
}

/**
 * How many recorded replacements to name, and how much of each. Two is enough to see a pattern and
 * cheap enough that the field cannot crowd the report's budget; 80 characters is comfortably past the
 * point where a diverging figure, date or label has identified itself.
 */
const MAX_NAMED_REPLACEMENTS = 2;
const MAX_NAMED_LENGTH = 80;

/**
 * The recorded `before -> after` pairs, rendered for a report.
 *
 * React does not give us this. In production `throwOnHydrationMismatch` builds `#418` as
 * `formatProdErrorMessage(418, isText ? 'text' : 'HTML', '')` — the second argument, the one that
 * carries the diff in development, is a hard-coded empty string, which is the trailing `args[]=` every
 * one of these tickets shows. So a text mismatch tells us only that *some* text differed.
 *
 * The recorder above has had the answer all along. React repairing a mismatch IS a `characterData`
 * mutation on a text node whose new value is not an extension of the old one, so it is logged like any
 * other, with `oldValue` the server's text and the new value the client's. {@link describeHydrationRewrite}
 * has only ever reported how MANY such records there were, never what they said — which is why the
 * `args[]=text` half of this family has been diagnosed three times from guesses and never from data.
 *
 * Truncation is marked with `…` rather than left silent: a value cut at the bound and a value that is
 * genuinely short must not read the same, or a diff will be blamed on the wrong end of the string.
 */
export function describeTextMismatch(replacements: readonly RecordedTextReplacement[]): string | undefined {
	if (replacements.length === 0) return undefined;
	const clip = (value: string): string =>
		value.length > MAX_NAMED_LENGTH ? `${value.slice(0, MAX_NAMED_LENGTH)}…` : value;
	const named = replacements
		.slice(0, MAX_NAMED_REPLACEMENTS)
		.map(({ before, after }) => `${JSON.stringify(clip(before))} -> ${JSON.stringify(clip(after))}`);
	// Say so when there were more, so two pairs are never mistaken for the whole story.
	const rest = replacements.length - named.length;
	return rest > 0 ? `${named.join(' ; ')} (+${rest} more)` : named.join(' ; ');
}

/** Whether a hydration-class error belongs to whatever rewrote this document, and why we think so. */
export interface HydrationRewriteVerdict {
	/** True when the mismatch is the rewriter's, not ours: skip the report. */
	readonly foreign: boolean;
	/** The reasoning, for the report's `counters` field. Always present, even when nothing was found. */
	readonly attribution: string;
	/**
	 * The text that actually changed, when anything was recorded.
	 *
	 * **It can only ever explain an `args[]=text` mismatch, and absence is NOT evidence of a pristine
	 * page.** The recorder observes `characterData` and nothing else, so a `#418 args[]=HTML` mismatch —
	 * a difference in elements rather than in text — leaves no record here by construction. A null value
	 * on one of those says only that the wrong instrument was read.
	 *
	 * Worth stating because the earlier wording ("absent on a pristine page — which is itself the
	 * finding") invited exactly the wrong inference: over the 30 days after this field shipped, every
	 * hydration report on `pro` was `args[]=HTML`, so the field was null on all of them and read as
	 * "nothing rewrote the page" when it meant "this cannot see structural mismatches". Localise one of
	 * those from the component stack instead — that is what names the element and the component.
	 */
	readonly mismatch: string | undefined;
}

/**
 * THE gate for hydration-class errors, and deliberately the only one. Every report path — the
 * `hydrateRoot` handlers in `client.tsx` and the two global handlers in `router.tsx` — asks this, so
 * they cannot drift apart again: the first version of this shipped the second signal to `client.tsx`
 * alone, leaving the global handlers on `isDocumentTranslated()` while their comments claimed parity,
 * so the text-only rewrites of TEC-3703 kept ticketing through them.
 *
 * Two signals, cheapest first:
 *
 *   - {@link isDocumentTranslated} catches translators that announce themselves: Chrome/Edge's
 *     `translated-*` class, or a rewritten `<html lang>`.
 *   - {@link wasOurTextReplaced} catches the ones that do not. A text-only rewrite leaves `<html lang>`
 *     and the class untouched while still reproducing `#418 args[]=text` exactly — verified against a
 *     production build on iOS (TEC-3703) — so the first signal is blind to it.
 *
 * Both fail open, so an unrecognised rewrite still reports.
 *
 * The attribution exists because "the page was pristine" and "the check never ran" are otherwise the
 * same silence, which is how this lineage (TEC-3481 → TEC-3703) stayed undiagnosable for so long.
 * `witnesses=0` says the cache lookup drifted — a `texts` shape change would disable the attribution
 * invisibly — and `replacements=0` on a ticket that keeps recurring says the monitor is not seeing the
 * rewrite. `pre=` is how many landed before `hydrateRoot`, the records that need no witness to be
 * trusted (see {@link HYDRATION_STARTED_KEY}). `capped=1` says the recorder hit {@link MAX_RECORDS}
 * and stopped, so `replacements=` is a floor and a rewrite may have landed unrecorded. Healthy numbers
 * on a report that still arrived mean the mismatch really was ours. (`witnesses=0` also shows,
 * expectedly, on shell errors that fire before the router has published its query client — the handle
 * is set inside `getRouter()`, which races the earliest hydration errors; that path fails open by
 * design.)
 *
 * It rides in the counters field, which is already free-form and already outside the reporter's
 * fingerprint, so it cannot mint a ticket per value.
 */
export function describeHydrationRewrite(queryClient: QueryClient | undefined): HydrationRewriteVerdict {
	const witnesses = renderedTextWitnesses(queryClient);
	const replacements = recordedTextReplacements();
	const preHydration = replacements.filter((replacement) => replacement.preHydration).length;
	return {
		foreign: isDocumentTranslated() || wasOurTextReplaced(replacements, witnesses),
		attribution: `rewriteCheck: witnesses=${witnesses.length} replacements=${replacements.length} pre=${preHydration} capped=${wasRewriteLogCapped() ? 1 : 0}`,
		mismatch: describeTextMismatch(replacements),
	};
}
